// My settings: time zone (dates are resolved in it) and the personal assistant's model.
import { useState } from 'react';
import { ErrorBox, Field, Loading, PageHead } from '../components/ui';
import { api, errorText } from '../lib/api';
import { useSession } from '../lib/session';
import type { Agent } from '../lib/types';
import { useApi } from '../lib/useApi';
import { AgentForm } from './Team';

export function Me() {
  const { me, refresh } = useSession();
  const assistant = useApi<Agent>('/api/me/assistant');
  const [tz, setTz] = useState(me.user.timezone);
  const [msg, setMsg] = useState<{ ok?: string; err?: string }>({});
  const [editing, setEditing] = useState(false);
  const zones = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [me.user.timezone];

  async function saveTz(e: React.FormEvent) {
    e.preventDefault();
    setMsg({});
    try {
      await api('/api/me', { method: 'PATCH', body: { timezone: tz } });
      await refresh();
      setMsg({ ok: 'Time zone saved.' });
    } catch (err) {
      setMsg({ err: errorText(err) });
    }
  }

  return (
    <>
      <PageHead title="My settings" sub={`${me.user.name} · ${me.user.email}`} />
      <section className="panel">
        <h2>Time zone</h2>
        <form className="row gap wrap" onSubmit={saveTz}>
          <Field label="Your time zone" hint="Used for “today”, due dates and, later, meeting dates.">
            <select value={tz} onChange={(e) => setTz(e.target.value)}>
              {(zones.includes(tz) ? zones : [tz, ...zones]).map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </Field>
          <button type="submit" className="btn">
            Save
          </button>
        </form>
        {msg.ok && <p className="msg ok">{msg.ok}</p>}
        {msg.err && <p className="msg err">{msg.err}</p>}
      </section>
      <section className="panel">
        <h2>Personal assistant</h2>
        {assistant.error && <ErrorBox error={assistant.error} retry={assistant.reload} />}
        {!assistant.data && !assistant.error && <Loading />}
        {assistant.data && (
          <>
            <p>{assistant.data.responsibilities}</p>
            <p className="small muted">
              model: <strong>{assistant.data.model ?? `installation default (${me.models.find((m) => m.default)?.label ?? '—'})`}</strong> · effort:{' '}
              <strong>{assistant.data.effort ?? 'default'}</strong>
            </p>
            <button type="button" className="btn" onClick={() => setEditing(true)}>
              Change model or instructions
            </button>
          </>
        )}
      </section>
      {editing && assistant.data && (
        <AgentForm
          agent={assistant.data}
          path="/api/me/assistant"
          personal
          onClose={() => setEditing(false)}
          onSaved={(a) => {
            setEditing(false);
            assistant.setData(a);
          }}
        />
      )}
    </>
  );
}
